import React from 'react';

import { AssetManager } from '../../../../viewer/asset-manager';
import { AssetCategory, ElementObj } from '../../../../viewer/elements/element-obj';
import { useSharedView } from '../../../hooks/use-shared-view';
import { useAssetGroupVisibility } from '../hooks/use-asset-group-visibility';

interface CategoryVisibilityContext {
  assets: ElementObj[];
  toggleVisibility: () => void;
  visible: boolean;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  height: number;
  categoryType: AssetCategory;
}

const CategoryVisibilityContext = React.createContext<CategoryVisibilityContext>(undefined!);

type Props = {
  assets: ElementObj[];
  categoryType: AssetCategory;
  height: number;
  children: React.ReactNode;
};

const CategoryVisibilityState = (props: Props) => {
  // Get visibility from asset manager. If undefined, use default true.
  const { visible, toggleVisibility } = useAssetGroupVisibility(
    props.assets,
    AssetManager.instance.isCategoryVisible(props.categoryType) ?? true,
  );
  const { isSharedView } = useSharedView();
  const [expanded, setExpanded] = React.useState(
    isSharedView || (AssetManager.instance.isCategoryExpanded(props.categoryType) ?? false),
  );

  React.useEffect(() => {
    AssetManager.instance.saveAssetCategoryExpanded(props.categoryType, expanded);
  }, [expanded, props.categoryType]);

  React.useEffect(() => {
    AssetManager.instance.saveAssetCategoryVisible(props.categoryType, visible);
  }, [visible, props.categoryType]);

  return (
    <CategoryVisibilityContext.Provider
      value={{
        assets: props.assets,
        visible,
        toggleVisibility,
        expanded,
        setExpanded,
        height: props.height,
        categoryType: props.categoryType,
      }}
    >
      {props.children}
    </CategoryVisibilityContext.Provider>
  );
};

export { CategoryVisibilityContext, CategoryVisibilityState };
